import {
  useApplication,
  ApplicationFormData,
  useCreateApplication,
  useUpdateApplication,
} from "@/api/application/application";
import { useLoggedInUser } from "@/api/user/user";
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Center,
  Flex,
  Link,
  Spinner,
  Text,
  VStack,
  useToast,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ConsentForm } from "../steps/ConsentForm";
import { defaultRegistryFormData, RegistryFormData } from "..";
import { consentCompletionErrors, stepCompletion } from "../validation";
import ErrorSummary from "../ErrorSummary";
import { useTranslation } from "react-i18next";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";

export const ConsentPage = () => {
  const applicationQuery = useApplication();
  const applicationData = applicationQuery.data;
  const { data: userData } = useLoggedInUser();
  const { mutateAsync: updateApplication } = useUpdateApplication();
  const { mutateAsync: createApplication } = useCreateApplication();

  const toast = useToast();
  const localizedNavigate = useLocalizedNavigate();
  const { t } = useTranslation(["registry", "common", "validation"]);

  const formHook = useForm<RegistryFormData>({
    defaultValues: defaultRegistryFormData,
    mode: "all",
    criteriaMode: "all",
  });

  const formData = useWatch({
    control: formHook.control,
  });
  const [hydratedFromApplicationId, setHydratedFromApplicationId] = useState<string | null>(null);

  useEffect(() => {
    if (applicationQuery.isLoading) return;
    if (applicationData === null) {
      setHydratedFromApplicationId(null);
      return;
    }
    if (!applicationData) return;
    const latest = formHook.getValues();
    formHook.reset({
      ...latest,
      application: {
        ...latest.application,
        ...applicationData,
      },
    });
    setHydratedFromApplicationId(applicationData.id);
  }, [applicationData, applicationQuery.isLoading, formHook]);

  const consentPreviouslyGranted = formData?.application?.consent_locked ?? false;

  const consentFields = useWatch({
    control: formHook.control,
    name: [
      "application.consent_health_collection",
      "application.consent_contact",
      "application.consent_contact_email",
      "application.consent_health_sharing",
    ],
  });

  const consentErrors = useMemo(() => {
    const currentValues = formHook.getValues();
    return consentCompletionErrors(currentValues.application);
  }, [consentFields, formHook]);

  const consentIsComplete = useMemo(() => {
    return !consentErrors || consentErrors.length === 0;
  }, [consentErrors]);

  const generalIsComplete = useMemo(() => {
    if (!applicationData) return false;
    const { general: generalComplete } = stepCompletion(applicationData);
    return generalComplete;
  }, [applicationData]);

  const isHydrated =
    applicationQuery.isLoading === false &&
    (applicationData === null ||
      (applicationData?.id && hydratedFromApplicationId === applicationData.id));

  const saveAndContinue = useCallback(async () => {
    if (!userData?.user?.id) return;
    if (!generalIsComplete) return;

    const valid = await formHook.trigger();
    if (!valid) return;

    const payload: ApplicationFormData = {
      ...formHook.getValues().application,
      user_id: userData.user.id,
      is_submitted: false,
    };

    try {
      if (payload.id) {
        await updateApplication({ id: payload.id!, ...payload });
      } else {
        await createApplication(payload);
      }
      toast({
        title: t("common:toast.saved"),
        description: t("registry:consent.consentSaved"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      localizedNavigate("/registry/medical");
      window.scrollTo(0, 0);
    } catch (error) {
      const message = error instanceof Error ? error.message : t("common:errors.unknownError");
      toast({
        title: t("common:toast.error"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    createApplication,
    formHook,
    localizedNavigate,
    toast,
    updateApplication,
    userData?.user?.id,
    generalIsComplete,
    t,
  ]);

  const handleContinue = useCallback(async () => {
    // Once consent is locked, this step is read-only; allow users to proceed without saving.
    if (consentPreviouslyGranted) {
      localizedNavigate("/registry/medical");
      window.scrollTo(0, 0);
      return;
    }
    await saveAndContinue();
  }, [consentPreviouslyGranted, localizedNavigate, saveAndContinue]);

  if (applicationQuery.isLoading) {
    return (
      <Center paddingY={16}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text fontSize="lg" color="gray.700">
            {t("common:status.loadingApplication")}
          </Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={6} alignItems="stretch" paddingBlockEnd={24}>
      <Flex fontSize="md" alignItems="baseline" gap={1} color="gray.600" fontWeight="semibold">
        <Link as={LocalizedLink} color="blue.500" to="/">
          &larr; {t("common:buttons.backToStepList")}
        </Link>
      </Flex>

      <ConsentForm
        consentPreviouslyGranted={consentPreviouslyGranted}
        isEditable={generalIsComplete && !consentPreviouslyGranted}
        formControl={formHook.control}
      />
      <Button
        data-testid="save-and-continue-button"
        colorScheme="blue"
        paddingInline={20}
        paddingY={8}
        fontSize="xl"
        width="full"
        maxWidth="container.md"
        marginInline="auto"
        onClick={handleContinue}
        isDisabled={!isHydrated || !consentIsComplete || !generalIsComplete}
      >
        {consentPreviouslyGranted
          ? t("common:buttons.continue")
          : t("common:buttons.saveAndContinue")}
      </Button>

      <ErrorSummary
        errors={consentErrors}
        extraMessages={
          generalIsComplete ? undefined : [t("validation:errorSummary.generalRequired")]
        }
      />
    </VStack>
  );
};
